import isNil from 'lodash/isNil';
import omitBy from 'lodash/omitBy';
import { useContext, useEffect, useMemo, useState } from 'react';
import { Union } from 'ts-toolbelt';

import { identify } from 'analytics/utils';
import { AuthContext } from 'contexts/Auth';
import { getAuthHeader } from 'util/authHeader';
import getMetaContent from 'util/metaContent';
import trackReferralProfile from 'util/trackReferralProfile';

import { ENDPOINT } from './constants';

interface UserDetails {
  uuid: string;
  email: Union.Nullable<string>;
  lifeApplicationId: string;
  lifeApplicationReferenceId: string;
  marketingCampaignId: Union.Nullable<string>;
}

export default () => {
  const [fetching, setFetching] = useState(true);
  const [currentUserDetails, setCurrentUserDetails] = useState<UserDetails>();
  const { auth } = useContext(AuthContext);

  useEffect(() => {
    if (auth.type) {
      const headers: Record<string, string> = {
        Accept: 'application/json',
      };

      if (auth.token) {
        headers.Authorization = getAuthHeader(auth)!;
      }

      const queryParams = new URLSearchParams(window.location.search);
      const agentUuid = queryParams.get('agent_uuid');

      // The marketing team calls this pathtype, as from their POV this determines which funnel experience their leads should see
      // In our db model, we store the pathtype param as marketing_campaign_id, to avoid tighly coupling with the UI/UX
      const marketingPathType = queryParams.get('pathtype');
      const searchParams = new URLSearchParams();

      if (agentUuid) {
        searchParams.append('agent_uuid', agentUuid);
      }

      if (marketingPathType) {
        searchParams.append('marketing_campaign_id', marketingPathType);
      }
      const queryEndpoint = `${ENDPOINT}?${searchParams}`;

      fetch(queryEndpoint, {
        credentials: 'same-origin',
        headers,
      })
        .then((res) => {
          if (res.ok) {
            return res.json();
          }

          throw `Error fetching current user: ${res.status} (${res.statusText})`;
        })
        .then(async (data) => {
          const {
            uuid,
            email,
            life_application_id,
            order_number,
            applied_annual_premium,
            life_application_reference_id,
            marketing_campaign_id,
          } = data;

          setCurrentUserDetails({
            uuid,
            email,
            lifeApplicationId: life_application_id.toString(),
            lifeApplicationReferenceId: life_application_reference_id,
            marketingCampaignId: marketing_campaign_id,
          });

          trackReferralProfile(auth);
          window.dataLayer.unshift(
            omitBy(
              {
                order_number,
                applied_annual_premium,
              },
              isNil
            )
          );
          identify(uuid, {
            userId: uuid,
            uuid,
            contentGroup: getMetaContent('contentGroup'),
            visitorId: queryParams.get('visitor_id'),
            webBrowserUuid: queryParams.get('web_browser_uuid'),
          });
          window.traits = {
            uuid,
          };
        })
        .catch(console.error)
        .finally(() => {
          setFetching(false);
        });
    }
  }, [auth.type]);

  return useMemo(
    () => ({
      fetching,
      currentUserDetails,
    }),
    [fetching, currentUserDetails]
  );
};
